import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ConfirmModal from './ConfirmModal';
import { UI_THEME } from '../ui/theme';
import { notifyError, notifySuccess } from '../utils/notify';
import { mensagemDeErro, solicitacaoService, type Solicitacao } from '../services/solicitacaoService';

type Acao = 'atender' | 'recusar';

/**
 * Painel de quem trata a solicitação. Assumir, responder, atender e recusar só registram o andamento e
 * avisam o parlamentar: nenhuma delas altera a emenda, o plano ou o pré-projeto.
 */
const PainelTratamentoSolicitacao: React.FC<{ solicitacao: Solicitacao }> = ({ solicitacao }) => {
  const queryClient = useQueryClient();
  const isSaas = UI_THEME === 'saas';
  const [mensagem, setMensagem] = useState('');
  const [confirmar, setConfirmar] = useState<Acao | null>(null);

  const aposAcao = (texto: string) => () => {
    queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
    notifySuccess(texto);
    setMensagem('');
    setConfirmar(null);
  };
  const aoFalhar = (err: unknown) => {
    setConfirmar(null);
    notifyError(mensagemDeErro(err, 'Não foi possível concluir a ação.'));
  };

  const assumir = useMutation({
    mutationFn: () => solicitacaoService.assumir(solicitacao.id),
    onSuccess: aposAcao('Solicitação assumida.'),
    onError: aoFalhar,
  });
  const responder = useMutation({
    mutationFn: () => solicitacaoService.responder(solicitacao.id, mensagem),
    onSuccess: aposAcao('Resposta enviada ao parlamentar.'),
    onError: aoFalhar,
  });
  const atender = useMutation({
    mutationFn: () => solicitacaoService.atender(solicitacao.id, mensagem),
    onSuccess: aposAcao('Solicitação atendida.'),
    onError: aoFalhar,
  });
  const recusar = useMutation({
    mutationFn: () => solicitacaoService.recusar(solicitacao.id, mensagem),
    onSuccess: aposAcao('Solicitação recusada.'),
    onError: aoFalhar,
  });

  const ocupado = assumir.isPending || responder.isPending || atender.isPending || recusar.isPending;
  const surface = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const primary = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const neutro = 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';
  const btn = 'rounded-xl px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50';

  if (solicitacao.status === 'ATENDIDA' || solicitacao.status === 'RECUSADA') return null;

  const texto = mensagem.trim();
  const escrever = (campo: string) => { if (!texto) notifyError(campo); return Boolean(texto); };

  return (
    <section className={`rounded-3xl border p-5 ${surface}`}>
      <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Tratar solicitação</h2>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
        {solicitacao.responsavelNome ? `Responsável: ${solicitacao.responsavelNome}.` : 'Ninguém assumiu ainda.'}{' '}
        Nenhuma ação aqui altera a emenda: a mudança em si segue o fluxo de aprovação.
      </p>

      <label htmlFor="trat-mensagem" className="mb-1 mt-4 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-400">
        Mensagem ao parlamentar
      </label>
      <textarea
        id="trat-mensagem"
        value={mensagem}
        maxLength={4000}
        onChange={(e) => setMensagem(e.target.value)}
        className="min-h-[96px] w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
      />
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Obrigatória para responder e para recusar (é o motivo da recusa); opcional ao atender.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {solicitacao.status === 'ABERTA' && (
          <button type="button" disabled={ocupado} onClick={() => assumir.mutate()} className={`${btn} ${neutro}`}>
            Assumir
          </button>
        )}
        <button type="button" disabled={ocupado}
          onClick={() => escrever('Escreva a resposta.') && responder.mutate()} className={`${btn} ${primary}`}>
          Responder
        </button>
        <button type="button" disabled={ocupado} onClick={() => setConfirmar('atender')}
          className={`${btn} border border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-50 dark:bg-slate-900 dark:text-emerald-300 dark:hover:bg-emerald-500/10`}>
          Atender
        </button>
        <button type="button" disabled={ocupado}
          onClick={() => escrever('Informe o motivo da recusa.') && setConfirmar('recusar')}
          className={`${btn} border border-red-600 bg-white text-red-700 hover:bg-red-50 dark:bg-slate-900 dark:text-red-300 dark:hover:bg-red-500/10`}>
          Recusar
        </button>
      </div>

      <ConfirmModal
        open={confirmar === 'atender'}
        title="Atender solicitação"
        body="A solicitação será encerrada como atendida e o parlamentar será avisado. Confirma?"
        onConfirm={() => atender.mutate()}
        onCancel={() => setConfirmar(null)}
      />
      <ConfirmModal
        open={confirmar === 'recusar'}
        title="Recusar solicitação"
        body="A solicitação será encerrada como recusada, com a mensagem como motivo, e o parlamentar será avisado. Confirma?"
        onConfirm={() => recusar.mutate()}
        onCancel={() => setConfirmar(null)}
      />
    </section>
  );
};

export default PainelTratamentoSolicitacao;
